import React from 'react';
import { Avatar, Dropdown, MenuProps, Switch, Spin } from 'antd';
import { useMemo, useState } from 'react';
import { IoIosArrowDown, IoIosArrowUp } from 'react-icons/io';
import { useDispatch, useSelector } from 'react-redux';
import { ReactComponent as Vector } from '../../assets/svgIcons/vector.svg';
import { getModuleByRole } from '../../helpers/get-module-by-role';
import { UserStateProps } from '../../interfaces/modules.interface';
import { setActiveUser } from '../../redux/features/activeUser/activeUser';
import SettingsModal from '../SettingsModal/SettingsModal';
import { useLogout } from '../../hooks/use-logout';
import { useLocation } from 'react-router';
import { useActivate2fa } from '../../hooks/use-activate-2fa';
import {
  capitalizeText,
  getActiveUser,
  getFirstCharacters,
  getFullName,
} from '../common/common-functions/common-functions';
import { useAuth } from '../../hooks/use-auth';
import { useRoleGetter } from '../../helpers/getUserInfo';

interface AccountProps {
  isMobile?: boolean;
}

const Account = ({ isMobile = false }: AccountProps) => {
  const [openSettingsModal, setOpenSettingsModal] = useState(false);
  const [showMenuItems, setShowMenuItems] = useState(false);

  const { pathname } = useLocation();

  const { data: loggedInUser } = useAuth();
  const dispatch = useDispatch();
  const selectorRole = useSelector((state: UserStateProps) => state.activeUser);
  const modules = getModuleByRole(loggedInUser?.rolet);
  const { handleSignOutWithApi, isLoading: isLoggingOut } = useLogout();
  const { activateTfa, isLoading } = useActivate2fa();
  const userRole = useRoleGetter();

  const fullName = getFullName(loggedInUser);

  const moduleAttributes: MenuProps['items'] = useMemo(
    () =>
      modules
        .filter(module => module.activeRole)
        .map((item, index) => ({
          label: (
            <div
              onClick={() => {
                if (item.activeRole && !isLoggingOut) {
                  dispatch(setActiveUser(item.activeRole));
                  localStorage.setItem('activeUser', item.activeRole);
                  localStorage.setItem('moduleUser', item.activeRole);
                }
              }}
              style={{
                backgroundColor: item.bgColor,
                color: item.color,
                borderRadius: 5,
                opacity: isLoggingOut ? 0.5 : 1,
                cursor: isLoggingOut ? 'not-allowed' : 'pointer',
              }}
              className="flex h-full w-full items-center p-2"
            >
              {item.label}
            </div>
          ),
          key: `${item.activeRole}${index}`,
          disabled: isLoggingOut,
        })),
    [modules, isLoggingOut],
  );

  const role = useMemo(
    () => (selectorRole.length > 0 ? selectorRole : getActiveUser()),
    [selectorRole],
  );

  const switchRoles = useMemo(() => {
    if (role) {
      return [
        {
          label: (
            <a
              href="#"
              className="flex items-center gap-1"
              onClick={e => {
                e.stopPropagation();
                if (!isLoggingOut) {
                  setShowMenuItems(state => !state);
                }
              }}
            >
              <p>Ndryshoni modulin</p>
              {showMenuItems ? <IoIosArrowUp /> : <IoIosArrowDown />}
            </a>
          ),
          key: '4',
          disabled: isLoggingOut,
        },
      ];
    }
    return [];
  }, [selectorRole, showMenuItems, isLoggingOut]);

  const showSwitch = !loggedInUser?.is2FAEnabled && !loggedInUser?.mfaEnabled;

  const accountSettings: MenuProps['items'] = [
    {
      label: (
        <a onClick={() => !isLoggingOut && setOpenSettingsModal(true)}>
          Cilësimet e llogarisë
        </a>
      ),
      key: '2',
      disabled: isLoggingOut,
    },
    {
      label: (
        <div className="flex items-center justify-between">
          <span
            className={
              !showSwitch
                ? '!cursor-text font-medium text-green-700'
                : '!cursor-text '
            }
          >
            {!showSwitch ? '2FA aktive' : 'Aktivizo 2FA'}
          </span>
          {showSwitch && (
            <Switch
              loading={isLoading}
              defaultChecked={!showSwitch}
              checked={!showSwitch}
              disabled={isLoggingOut}
              onChange={e => {
                if (e && !isLoggingOut) {
                  activateTfa();
                }
              }}
            />
          )}
        </div>
      ),
      key: '3',
      disabled: isLoggingOut,
    },
    ...switchRoles,
    ...(showMenuItems ? moduleAttributes : []),
    {
      type: 'divider',
    },
  ];

  const items: MenuProps['items'] = [
    {
      label: (
        <div className="grid">
          <p className="font-normal">{fullName}</p>
          <p className="font-medium">{loggedInUser?.email}</p>
          {userRole && (
            <p className="text-sm text-gray-500 mt-1">
              {capitalizeText(userRole, true)}
            </p>
          )}
        </div>
      ),
      key: '0',
      disabled: isLoggingOut,
    },
    {
      type: 'divider',
    },
    ...(!loggedInUser?.firstLogIn && pathname !== '/two-fa'
      ? accountSettings
      : []),
    {
      label: (
        <a onClick={() => !isLoggingOut && handleSignOutWithApi()}>
          {isLoggingOut ? (
            <span className="flex items-center gap-2">
              <Spin size="small" />
              Duke dalë...
            </span>
          ) : (
            'Dilni'
          )}
        </a>
      ),
      key: '5',
      disabled: isLoggingOut,
    },
  ];

  if (!loggedInUser) {
    return null;
  }

  if (isMobile) {
    return (
      <>
        {openSettingsModal && (
          <SettingsModal
            openSettingsModal={openSettingsModal}
            setOpenSettingsModal={setOpenSettingsModal}
            isMobile
          />
        )}
        <Dropdown menu={{ items }} trigger={['click']} placement="bottomRight">
          <div className="flex items-center cursor-pointer">
            <Avatar
              style={{ backgroundColor: '#f56a00' }}
              size="default"
              className="flex-shrink-0"
            >
              {getFirstCharacters(fullName)}
            </Avatar>
          </div>
        </Dropdown>
      </>
    );
  }

  return (
    <>
      {openSettingsModal && (
        <SettingsModal
          openSettingsModal={openSettingsModal}
          setOpenSettingsModal={setOpenSettingsModal}
        />
      )}
      <div className="flex gap-4 items-center">
        <Avatar
          style={{ backgroundColor: '#f56a00' }}
          size="large"
          className="flex-shrink-0"
        >
          {getFirstCharacters(fullName)}
        </Avatar>
        <div className="truncate flex flex-col">
          <p className="text-base font-normal">{fullName}</p>
          {userRole && (
            <p className="text-sm text-gray-500">
              {capitalizeText(userRole, true)}
            </p>
          )}
        </div>
        <Dropdown
          menu={{ items }}
          trigger={['click']}
          className="flex-shrink-0 pb-5"
        >
          <a onClick={e => e.preventDefault()}>
            <Vector />
          </a>
        </Dropdown>
      </div>
    </>
  );
};

export default Account;
